import { useCallback, useMemo, useState } from 'react'
import type { JSX } from 'react'

import { Button, ClockIcon, Dialog, Notice, toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { FeedRow } from '~publish/pages/Community/components/FeedRow'
import type { FilterStatus } from '~publish/pages/Community/CommunityFilters/types'

import { getCurrentMonthKey } from '../../util/dateHelpers'
import { useOldCommentsCheck } from './hooks'
import { useMonthlyCleanupResolve } from './hooks/useMonthlyCleanupResolve'
import { MonthlyCleanupDialog } from './MonthlyCleanupDialog'
import { MonthlyCleanupSuccess } from './MonthlyCleanupSuccess'
import type { MonthlyCleanupChannel } from './useMonthlyCleanupDialog'

import styles from './MonthlyCleanupBanner.module.css'

/** Channel fields the cleanup banner and its dialog render for each channel. */
export const MonthlyCleanupBanner_Channel = graphql(/* GraphQL */ `
  fragment MonthlyCleanupBanner_Channel on Channel @_unmask {
    id
    name
    service
    avatar
  }
`)

type MonthlyCleanupBannerChannel = FragmentOf<
  typeof MonthlyCleanupBanner_Channel
>

/**
 * Status filters the cleanup prompt shows under — the ones whose feed can hold
 * unanswered comments. The old-comment count is always unanswered-only, so the
 * banner's numbers mean the same thing under either.
 */
export const isMonthlyCleanupStatus = (
  status: FilterStatus | undefined,
): boolean => status === 'unreplied' || status === 'all'

/**
 * The banner's dismissal is keyed by year-month so it silences only the month
 * it's dismissed in and returns at the start of the next month.
 */
export const useMonthlyCleanupBanner = (): ReturnType<
  typeof useDismissableBanner
> => {
  return useDismissableBanner(
    `resolve-old-comments-banner-${getCurrentMonthKey()}`,
    { trackDismiss: true },
  )
}

type MonthlyCleanupBannerProps = {
  channels: MonthlyCleanupBannerChannel[]
  onBannerDismissed?: () => void
}

/**
 * Monthly prompt to resolve unanswered comments older than one month. When more
 * than one channel has old comments it opens a channel-picker dialog; with a
 * single channel it resolves inline and shows a success dialog.
 */
export const MonthlyCleanupBanner = ({
  channels,
  onBannerDismissed,
}: MonthlyCleanupBannerProps): JSX.Element | null => {
  const banner = useMonthlyCleanupBanner()

  const channelIds = useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )
  const { channelsWithOldComments } = useOldCommentsCheck({ channelIds })

  const channelById = useMemo(
    () => new Map(channels.map((channel) => [channel.id, channel])),
    [channels],
  )

  const cleanupChannels = useMemo<MonthlyCleanupChannel[]>(
    () =>
      channelsWithOldComments.flatMap(({ channelId, count, limit }) => {
        const channel = channelById.get(channelId)
        if (!channel) return []
        return [
          {
            id: channel.id,
            name: channel.name ?? '',
            service: channel.service,
            avatar: channel.avatar ?? '',
            count,
            limit,
          },
        ]
      }),
    [channelsWithOldComments, channelById],
  )

  const [pickerOpen, setPickerOpen] = useState(false)
  const [successOpen, setSuccessOpen] = useState(false)
  const [singleResolving, setSingleResolving] = useState(false)
  const [singleTotal, setSingleTotal] = useState(0)

  const { resolveChannels } = useMonthlyCleanupResolve()

  const handleDismiss = useCallback((): void => {
    banner.dismiss()
    onBannerDismissed?.()
  }, [banner, onBannerDismissed])

  const handleResolveSingle = useCallback(async (): Promise<void> => {
    const channel = cleanupChannels[0]
    if (!channel || singleResolving) return

    setSingleResolving(true)
    const { resolvedChannelIds, hasFailure } = await resolveChannels([
      channel.id,
    ])
    setSingleResolving(false)

    if (resolvedChannelIds.length > 0) {
      setSingleTotal(channel.count)
      setSuccessOpen(true)
      handleDismiss()
    }
    if (hasFailure) {
      toast.error('An error occurred while resolving the comments')
    }
  }, [cleanupChannels, singleResolving, resolveChannels, handleDismiss])

  const isMultiChannel = cleanupChannels.length > 1
  const noticeVisible = banner.isActive && cleanupChannels.length > 0
  const flowActive = pickerOpen || successOpen || singleResolving

  const singleChannel = cleanupChannels[0]
  const singleCount = singleChannel?.count ?? 0
  const singleCountLabel =
    singleChannel && singleCount >= singleChannel.limit
      ? `${singleChannel.limit}+`
      : `${singleCount}`
  const singlePlural = singleCount === 1 ? '' : 's'

  const noticeText = isMultiChannel ? (
    'Some of your channels have comments older than a month. Resolving them keeps your inbox focused on newer conversations.'
  ) : (
    <>
      {`${singleCountLabel} comment${singlePlural} `}
      <strong>on this channel</strong>
      {` ${singleCount === 1 ? 'is' : 'are'} older than one month. Resolving ${
        singleCount === 1 ? 'it' : 'them'
      } helps you focus on new conversations. You will be able to access resolved comments using the 'status' filter.`}
    </>
  )

  // Nothing to show and no flow running — render nothing. The dialogs below stay
  // mounted while a flow is active so completion screens survive the live counts
  // dropping to zero as comments get resolved.
  if (!noticeVisible && !flowActive) {
    return null
  }

  return (
    <>
      {noticeVisible && (
        <FeedRow>
          <Notice
            variant="neutral"
            icon={<ClockIcon />}
            onDismiss={handleDismiss}
          >
            <Notice.Heading>
              Start this month focused on what matters
            </Notice.Heading>
            <Notice.Text>{noticeText}</Notice.Text>
            {/* Not Notice.Actions: it would override these buttons' variants. */}
            <div className={styles.actions}>
              {isMultiChannel ? (
                <Button
                  size="small"
                  variant="primary"
                  onClick={() => setPickerOpen(true)}
                >
                  Select channels
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="primary"
                  disabled={singleResolving}
                  onClick={() => void handleResolveSingle()}
                >
                  {singleResolving
                    ? 'Resolving…'
                    : `Resolve ${singleCountLabel} comment${singlePlural}`}
                </Button>
              )}
              <Button size="small" variant="tertiary" onClick={handleDismiss}>
                Maybe Later
              </Button>
            </div>
          </Notice>
        </FeedRow>
      )}

      <MonthlyCleanupDialog
        channels={cleanupChannels}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onResolved={handleDismiss}
      />

      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <Dialog.Content size="medium">
          <MonthlyCleanupSuccess
            total={singleTotal}
            onClose={() => setSuccessOpen(false)}
          />
        </Dialog.Content>
      </Dialog>
    </>
  )
}
